/**
 * AddPhaseModal Component
 * Modal for creating custom phases
 */

import { useState } from 'react';
import { Modal, Stack, TextInput, Textarea, Button, Group } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { taskPhasesApi } from '../../../../services/api/taskPhases';
import type { TaskPhaseRecord } from '../../../../types';

interface AddPhaseModalProps {
  opened: boolean;
  onClose: () => void;
  onCreated: (phase: TaskPhaseRecord) => void;
}

export function AddPhaseModal({ opened, onClose, onCreated }: AddPhaseModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!name.trim()) return;

    setSaving(true);
    try {
      const phase = await taskPhasesApi.create({
        name: name.trim(),
        description: description.trim() || undefined,
      });

      notifications.show({
        title: 'Success',
        message: `Phase "${phase.name}" created successfully`,
        color: 'green',
      });

      // Reset form
      setName('');
      setDescription('');

      // Notify parent
      onCreated(phase);
      onClose();
    } catch (error) {
      console.error('Failed to create phase:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to create phase',
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setName('');
    setDescription('');
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Add Phase"
      size="md"
    >
      <Stack gap="md">
        <TextInput
          label="Phase Name"
          placeholder="e.g., Planning, Implementation, Review"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          data-autofocus
        />

        <Textarea
          label="Description"
          placeholder="Optional description for this phase"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          minRows={2}
        />

        <Group justify="flex-end" gap="sm" className="modal-footer">
          <Button variant="subtle" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            loading={saving}
            disabled={!name.trim()}
            color="medleyGreen"
          >
            Create Phase
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export default AddPhaseModal;
